/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import { io } from 'socket.io-client';
import { recoverWebsocketMessages } from '../utils/messaging-helpers';

export default class WebSocketUtils {
  constructor(serverUrl, sessionId) {
    this.serverUrl = serverUrl;
    this.sessionId = sessionId;

    this.socket = null;
    this.referenceCount = 0;

    // Keep track of subscribed resource
    this.subscribedResource = null;

    // Keep track of message subscriber
    this.subscriber = null;

    // Connection loss state
    this.wasConnectionLost = false;
  }

  connect() {
    if (this.socket) {
      return;
    }

    this.socket = io(this.serverUrl, {
      transports: ['websocket'],
      auth: { token: `Bearer chatbot_${this.sessionId}` },
      reconnection: true,
      reconnectionAttempts: Infinity
    });

    this.socket.on('connect_error', error => {
      console.error('Socket connection error:', error);
      this.wasConnectionLost = true;
    });

    this.socket.on('connect', async () => {
      console.log('Socket connected');
      // Resubscribe after connection was lost
      if (this.wasConnectionLost) {
        await recoverWebsocketMessages(this.sessionId);
        this.subscribe(this.subscribedResource, this.subscriber);
        this.wasConnectionLost = false;
      }
    });

    this.socket.on('disconnect', reason => {
      console.log('Socket disconnected:', reason);
      if (reason === 'ping timeout') {
        this.wasConnectionLost = true;
      }
    });

    this.socket.on('message', data => {
      if (this.subscriber) {
        this.subscriber(data);
      }
    });
  }

  subscribe(resource, callback) {
    console.log('Socket subscribed to resource:', resource);

    this.subscribeToResource(resource);

    this.emit('subscribe', resource);

    this.connect();

    this.referenceCount++;

    this.subscriber = callback;

    // Return cleanup function, which works nicely with React useEffect
    return () => {
      this.unsubscribe(callback);
    };
  }

  unsubscribe(callback) {
    if (callback) {
      this.subscriber = null;
    }

    this.referenceCount--;

    if (this.referenceCount <= 0) {
      this.referenceCount = 0;
      this.disconnect();
    }
  }

  subscribeToResource(resource) {
    this.subscribedResource = resource;
  }

  unsubscribeToResource(resource) {
    this.subscribedResource = null;

    this.emit('unsubscribe', resource);
  }

  emit(event, data) {
    if (!this.socket) {
      return;
    }

    this.socket.emit(event, data);
  }

  send(data) {
    if (!this.socket) {
      return;
    }

    this.socket.emit('message', data);
  }

  disconnect() {
    if (this.socket && this.referenceCount === 0) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected() {
    return this.socket?.connected ?? false;
  }

  destroy() {
    this.referenceCount = 0;
    this.disconnect();
  }
}
